<template>
  <Modal
    v-model="visible"
    class-name="short-review-preview-dialog"
    title="在线预览"
    width="1000"
    :styles="{ top: '40px' }"
    :mask-closable="false"
    @on-cancel="handleClose"
  >
    <div class="preview-dialog-body">
      <div class="preview-main">
        <div class="preview-player-column">
          <VideoPlayer
            v-if="currentVideoUrl"
            :key="videoKey"
            ref="videoPlayerRef"
            class="preview-series-player"
            :url="currentVideoUrl"
            :poster-url="currentSeries.posterFileUrl"
            preview-subtitle-tabs
            use-hls-embedded-subtitles
            :subtitles="previewSubtitles"
            v-model="currentSubtitle"
          />
          <div v-else class="preview-player-empty">
            <div class="preview-player-title preview-player-title--empty">
              {{ displayTitle }}
            </div>
            <div class="preview-player-empty-text">暂无预览文件</div>
            <div class="preview-player-episode preview-player-episode--empty">
              第 {{ currentEpisodeNum }} 集 · 第 {{ currentEpisodeNum }} 集
            </div>
          </div>
        </div>

        <div class="preview-episode-panel">
          <div v-if="!episodeList.length" class="preview-episode-empty">
            暂无集数数据
          </div>
          <div v-else class="preview-episode-scroll">
            <div
              v-for="(item, index) in episodeList"
              :key="item.seriesName + '-' + index"
              class="preview-episode-cell"
            >
              <div
                class="preview-episode-btn"
                :class="{
                  'preview-episode-btn--active':
                    currentSeries.seriesNum == item.seriesNum,
                  'preview-episode-btn--disabled': !item.fileUrl
                }"
                @click="selectSeries(item)"
              >
                <span class="preview-episode-num">{{ item.seriesNum }}</span>
                <Icon
                  v-show="item.lockFlag"
                  type="md-lock"
                  class="preview-episode-lock"
                  :size="10"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <Button @click="handleClose">关闭</Button>
    </template>
  </Modal>
</template>

<script>
import CryptoJS from 'crypto-js'
import VideoPlayer from '@/components/VideoPlayer'
import {
  SUBTITLE_OFF,
  extractSubtitlesFromSeriesItem
} from '@/utils/previewSubtitle'

export default {
  components: {
    VideoPlayer
  },
  props: {
    data: {
      type: Object,
      default: () => ({
        chinesePlayName: '',
        playId: '',
        playName: '',
        posterFileUrl: '',
        seriesList: []
      })
    }
  },
  data() {
    return {
      visible: false,
      currentSeries: {},
      previewSubtitles: [],
      currentSubtitle: SUBTITLE_OFF,
      videoKey: 0
    }
  },
  computed: {
    episodeList() {
      return (this.data && this.data.seriesList) || []
    },
    displayTitle() {
      const d = this.data || {}
      return d.chinesePlayName || d.playName || '—'
    },
    currentEpisodeNum() {
      const n = Number(this.currentSeries && this.currentSeries.seriesNum)
      return !Number.isNaN(n) && n > 0 ? n : 1
    },
    currentVideoUrl() {
      const url = this.resolveMediaUrl(this.currentSeries.fileUrl)
      return url ? this.getUrl(url) : ''
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.initCurrentSeries()
      } else {
        this.resetPreviewState()
      }
    },
    currentSeries: {
      handler(item) {
        if (!this.visible) return
        this.syncSubtitlesFromSeries(item)
        this.videoKey += 1
      },
      deep: true
    }
  },
  methods: {
    resetPreviewState() {
      this.pauseVideo()
      this.previewSubtitles = []
      this.currentSubtitle = SUBTITLE_OFF
      this.currentSeries = {}
      this.videoKey += 1
    },
    handleClose() {
      this.pauseVideo()
      this.visible = false
    },
    initCurrentSeries() {
      const list = this.episodeList
      const first = list.find((item) => item && item.fileUrl) || list[0] || {}
      this.currentSeries = first.fileUrl ? { ...first } : {}
      if (!first.fileUrl) {
        this.previewSubtitles = []
        this.currentSubtitle = SUBTITLE_OFF
      }
    },
    selectSeries(item) {
      if (!item || !item.fileUrl) return
      if (this.currentSeries.seriesNum === item.seriesNum) return
      this.pauseVideo()
      this.currentSeries = { ...item }
    },
    syncSubtitlesFromSeries(seriesItem) {
      const raw = extractSubtitlesFromSeriesItem(seriesItem)
      this.previewSubtitles = raw
        .map((sub) => {
          const decrypted = this.resolveMediaUrl(sub.subtitleUrl)
          const subtitleUrl = decrypted ? this.getUrl(decrypted) : ''
          return subtitleUrl ? { ...sub, subtitleUrl } : null
        })
        .filter(Boolean)
    },
    pauseVideo() {
      const wrapper = this.$refs.videoPlayerRef
      if (wrapper && typeof wrapper.pause === 'function') {
        wrapper.pause()
      }
    },
    resolveMediaUrl(encryptedOrPlain) {
      if (!encryptedOrPlain) return ''
      const decrypted = this.decrypt(encryptedOrPlain)
      return decrypted || String(encryptedOrPlain).trim()
    },
    getUrl(url) {
      return url
    },
    decrypt(str) {
      if (!str) return ''
      const de = CryptoJS.AES.decrypt(
        str,
        CryptoJS.enc.Base64.parse('M28IxuBkkXxklZYAylvojA=='),
        {
          mode: CryptoJS.mode.ECB,
          padding: CryptoJS.pad.Pkcs7
        }
      )
      return de.toString(CryptoJS.enc.Utf8)
    }
  }
}
</script>

<style scoped>
.preview-dialog-body {
  position: relative;
}

.preview-main {
  display: flex;
  align-items: flex-start;
  min-height: 700px;
}

.preview-player-column {
  position: relative;
  flex-shrink: 0;
  width: 393.75px;
  margin-right: 20px;
  overflow: visible;
}

.preview-series-player {
  width: 100%;
  height: auto;
}

.preview-series-player >>> .video-player-root--preview-tabs .video-player-wrapper {
  height: 700px;
}

.preview-series-player >>> .video-player-wrapper .video-js {
  width: 100%;
  height: 100%;
}

.preview-player-empty {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 700px;
  padding: 24px 16px 20px;
  box-sizing: border-box;
  color: #fff;
  text-align: center;
  background: linear-gradient(180deg, #1a2744 0%, #2f4a7a 48%, #1e3358 100%);
}

.preview-player-title {
  font-size: 15px;
  font-weight: 600;
  line-height: 22px;
}

.preview-player-title--empty {
  position: absolute;
  top: 20px;
  left: 16px;
  right: 16px;
}

.preview-player-empty-text {
  font-size: 14px;
  line-height: 22px;
  opacity: 0.85;
}

.preview-player-episode {
  font-size: 13px;
  line-height: 20px;
  opacity: 0.9;
}

.preview-player-episode--empty {
  position: absolute;
  bottom: 18px;
  left: 16px;
  right: 16px;
}

.preview-episode-panel {
  flex: 1;
  min-width: 0;
  height: 700px;
  min-height: 700px;
  overflow: hidden;
}

.preview-episode-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  font-size: 14px;
  color: #909399;
}

.preview-episode-scroll {
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
  align-items: flex-start;
  width: 100%;
  height: 100%;
  margin: 5px -5px;
  overflow-x: hidden;
  overflow-y: auto;
  box-sizing: border-box;
}

.preview-episode-cell {
  width: calc(100% / 11);
  padding: 5px;
  box-sizing: border-box;
  line-height: normal;
}

.preview-episode-btn {
  position: relative;
  width: 100%;
  height: 0;
  padding-bottom: 100%;
  border: 1px solid #aaa;
  border-radius: 3px;
  cursor: pointer;
  font-size: 15px;
  color: #303133;
  background: #fff;
  box-sizing: border-box;
}

.preview-episode-btn--active {
  background-color: #2d98f1;
  color: #fff;
  border-color: #2d98f1;
}

.preview-episode-btn--disabled {
  background-color: #ccc;
  border-color: #ccc;
  color: #fff;
  cursor: not-allowed;
}

.preview-episode-num {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
}

.preview-episode-lock {
  position: absolute;
  right: 2px;
  top: 2px;
  z-index: 2;
}
</style>

<style>
.short-review-preview-dialog .ivu-modal {
  width: 1000px !important;
}

.short-review-preview-dialog .ivu-modal-content {
  width: 1000px !important;
}

.short-review-preview-dialog .ivu-modal-header {
  height: 76px;
  padding: 0 24px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  border-bottom: 1px solid #e8eaec;
}

.short-review-preview-dialog .ivu-modal-header-inner {
  font-size: 18px;
  font-weight: 600;
  color: #263f58;
  line-height: 24px;
}

.short-review-preview-dialog .ivu-modal-body {
  padding: 20px 24px 16px;
}

.short-review-preview-dialog .ivu-modal-footer {
  height: 66px;
  padding: 0 24px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  border-top: 1px solid #e8eaec;
}
</style>
